import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { logoutUser, hasRole } from '@3dera/core/services/auth';
import { getPageVisibilitySettings } from '@3dera/core/services/admin';
import { getTotalUnreadCount } from '@3dera/core/services/messages';
import { useNotification } from '../../context/NotificationContext';
import { useLanguage } from '../../context/LanguageContext';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import styles from './Navbar.module.css';

const CartButton = () => {
  const { itemCount } = useCart();
  return (
    <Link to="/cart" className={styles.cartButton} title="Sepetim">
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="22" height="22">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
      {itemCount > 0 && (
        <span className={styles.cartBadge}>{itemCount > 99 ? '99+' : itemCount}</span>
      )}
    </Link>
  );
};

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { t, language, toggleLanguage } = useLanguage();
  const { currentUser, userData, isAuthenticated, myStoreId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showMobileMenu]);
  const DEFAULT_PAGE_VISIBILITY = {
    home: true,
    marketplace: true,
    sellers: true,
    about: true,
    contact: true,
    becomeSeller: true,
    photoTo3D: true,
  };

  const getInitialVisibility = () => {
    try {
      const cached = localStorage.getItem('pageVisibilitySettings');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') return { ...DEFAULT_PAGE_VISIBILITY, ...parsed };
      }
    } catch (e) {
      // Cache yoksa varsayılanları kullan
    }
    return { ...DEFAULT_PAGE_VISIBILITY };
  };

  const [pageVisibility, setPageVisibility] = useState(getInitialVisibility);
  const mergeVisibility = (next) => {
    if (!next || typeof next !== 'object') return { ...DEFAULT_PAGE_VISIBILITY };
    return { ...DEFAULT_PAGE_VISIBILITY, ...next };
  };
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { notifications: rawNotifications, unreadCount: unreadNotifications, markAsRead, markAllAsRead } = useNotification();
  const dropdownRef = useRef(null);

  // Mesaj okunmamış sayısı
  useEffect(() => {
    if (!currentUser) return;
    const unsub = getTotalUnreadCount(currentUser.uid, myStoreId, setUnreadMessages);
    return () => unsub();
  }, [currentUser, myStoreId]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowNotifications(false);
      }
    };

    if (showDropdown || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showNotifications]);

  // Sayfa görünürlük ayarlarını yükle ve event dinle
  useEffect(() => {
    const loadPageVisibility = async () => {
      const result = await getPageVisibilitySettings();
      if (result.success && result.data) {
        setPageVisibility(mergeVisibility(result.data));
        // Cache'i güncelle
        try {
          localStorage.setItem('pageVisibilitySettings', JSON.stringify(result.data));
          localStorage.setItem('pageVisibilitySettingsTimestamp', Date.now().toString());
        } catch (e) {
          // localStorage hatası, devam et
        }
      }
    };

    // İlk yükleme (cache zaten state'te var, bu sadece güncelleme için)
    loadPageVisibility();

    // Admin bir değişiklik yaptığında tetiklenen eventi dinle
    const handleVisibilityUpdate = (event) => {
      if (event.detail?.settings) {
        setPageVisibility(mergeVisibility(event.detail.settings));
        // Cache'i güncelle
        try {
          localStorage.setItem('pageVisibilitySettings', JSON.stringify(event.detail.settings));
          localStorage.setItem('pageVisibilitySettingsTimestamp', Date.now().toString());
        } catch (e) {
          // localStorage hatası, devam et
        }
      } else {
        // Event'te settings yoksa tekrar yükle
        loadPageVisibility();
      }
    };

    window.addEventListener('pageVisibilitySettingsUpdated', handleVisibilityUpdate);

    // Sayfa görünür olduğunda ve focus aldığında kontrol et (kullanıcı başka sekmeden döndüğünde)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadPageVisibility();
      }
    };

    const handleFocus = () => {
      loadPageVisibility();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('pageVisibilitySettingsUpdated', handleVisibilityUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLoginClick = () => {
    navigate('/auth');
  };

  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      setShowDropdown(false);
      navigate('/');
    } else {
      setShowDropdown(false);
      toast.error(result.error || 'Çıkış yapılırken bir hata oluştu.');
    }
  };

  const getDisplayName = () => {
    if (userData?.firstName) {
      return userData.firstName;
    }
    if (currentUser?.displayName) {
      return currentUser.displayName.split(' ')[0];
    }
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'Kullanıcı';
  };

  // Admin, Moderator ve Satıcı kontrolü
  const isAdmin = userData?.roles && hasRole(userData.roles, 'admin');
  const isModerator = userData?.roles && hasRole(userData.roles, 'moderator');
  const isAdminOrModerator = isAdmin || isModerator;
  const isSeller = userData?.roles && hasRole(userData.roles, 'seller');

  // Bildirim yardımcıları
  const visibleNotifications = rawNotifications.filter(
    n => n.status !== 'deleted' && n.status !== 'archived'
  );

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_seller_application': return '🏪';
      case 'new_product_submission':
      case 'product_edit_request': return '📦';
      case 'new_profile_image': return '🖼️';
      case 'order_update': return '🛒';
      case 'sale_completed': return '💰';
      case 'feedback': return '💬';
      case 'seller_approved':
      case 'product_approved':
      case 'product_edit_approved':
      case 'profile_image_approved': return '✅';
      case 'seller_rejected':
      case 'product_rejected':
      case 'product_edit_rejected':
      case 'profile_image_rejected': return '❌';
      case 'product_deleted': return '🗑️';
      case 'seller_name_change_request': return '✏️';
      default: return '🔔';
    }
  };

  const formatNotifDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Az önce';
    if (minutes < 60) return `${minutes} dk önce`;
    if (hours < 24) return `${hours} sa önce`;
    if (days < 7) return `${days} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const handleNotificationClick = async (notification) => {
    if (notification.status === 'unread') await markAsRead(notification.id);
    if (notification.metadata?.link) {
      navigate(notification.metadata.link);
      setShowNotifications(false);
      setShowDropdown(false);
    }
  };

  return (
    <nav className={styles.navbar}>
      <div className={styles.container}>
        <div className={styles.navContent}>
          {/* Logo */}
          <div className={styles.logo}>
            <Link to="/" className={styles.logo}>
              <h1>
                <span className={styles.logo3d}>3D</span>
                <span className={styles.logoEra}>Era</span>
              </h1>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className={styles.navLinks}>
            {pageVisibility?.home !== false && (
              <Link 
                to="/" 
                className={`${styles.link} ${isActive('/') ? styles.active : ''}`}
              >
                {t('navbar.home')}
              </Link>
            )}
            {pageVisibility?.marketplace !== false && (
              <Link
                to="/marketplace"
                className={`${styles.link} ${isActive('/marketplace') ? styles.active : ''}`}
              >
                {t('navbar.gallery')}
              </Link>
            )}
            {pageVisibility?.sellers !== false && (
              <Link
                to="/sellers"
                className={`${styles.link} ${isActive('/sellers') ? styles.active : ''}`}
              >
                {t('navbar.sellers')}
              </Link>
            )}
            {pageVisibility?.about !== false && (
              <Link
                to="/about"
                className={`${styles.link} ${isActive('/about') ? styles.active : ''}`}
              >
                {t('navbar.about')}
              </Link>
            )}
            {pageVisibility?.contact !== false && (
              <Link
                to="/contact"
                className={`${styles.link} ${isActive('/contact') ? styles.active : ''}`}
              >
                {t('navbar.contact')}
              </Link>
            )}
            
            {/* Giriş yapılmamışsa sadece "Giriş Yap" butonu (sepet yok) */}
            {!isAuthenticated ? (
              <>
                <button
                  className={styles.loginButton}
                  onClick={handleLoginClick}
                >
                  {t('navbar.signIn')}
                </button>
                <button onClick={toggleTheme} className={styles.themeToggle}>
                  {theme === 'dark' ? '☀️' : '🌙'}
                </button>
                <button onClick={toggleLanguage} className={styles.langToggle} title="Change language">
                  {language === 'tr' ? 'EN' : 'TR'}
                </button>
              </>
            ) : (
              /* Giriş yapılmışsa sepet, mesaj butonu ve kullanıcı menüsü */
              <>
                {/* Sepet butonu */}
                <CartButton />

                {/* Mesaj butonu - Tüm giriş yapmış kullanıcılar */}
                <Link to="/mesajlar" className={styles.messagesButton} title={t('navbar.messages')}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {unreadMessages > 0 && (
                    <span className={styles.messagesBadge}>
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </Link>

                <div className={styles.userMenu} ref={dropdownRef}>
                <button
                  className={styles.userButton}
                  onClick={() => {
                    setShowDropdown(!showDropdown);
                    setShowNotifications(false);
                  }}
                >
                  <div className={styles.userAvatar}>
                    {currentUser?.photoURL ? (
                      <img
                        src={currentUser.photoURL}
                        alt="Avatar"
                        loading="eager"
                        fetchPriority="high"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span style={{ display: currentUser?.photoURL ? 'none' : 'flex' }}>
                      {getDisplayName().charAt(0).toUpperCase()}
                    </span>
                    {(unreadNotifications > 0 || unreadMessages > 0) && (
                      <span className={styles.avatarBadge} />
                    )}
                  </div>
                </button>

                {/* Bildirim paneli */}
                {showNotifications && (
                  <div className={styles.dropdown}>
                    <div className={styles.notifHeader}>
                      <button
                        className={styles.notifBackBtn}
                        onClick={() => {
                          setShowNotifications(false);
                          setShowDropdown(true);
                        }}
                      >
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className={styles.notifTitle}>Bildirimler</span>
                      {unreadNotifications > 0 && (
                        <button
                          className={styles.notifMarkAll}
                          onClick={() => markAllAsRead()}
                        >
                          Tümünü oku
                        </button>
                      )}
                    </div>
                    <div className={styles.notifList}>
                      {visibleNotifications.length === 0 ? (
                        <div className={styles.notifEmpty}>
                          <span>🔕</span>
                          <p>Henüz bildirim yok</p>
                        </div>
                      ) : (
                        visibleNotifications.slice(0, 10).map((n) => (
                          <button
                            key={n.id}
                            className={`${styles.notifItem} ${n.status === 'unread' ? styles.notifUnread : ''}`}
                            onClick={() => handleNotificationClick(n)}
                          >
                            <span className={styles.notifIcon}>{getNotificationIcon(n.type)}</span>
                            <div className={styles.notifContent}>
                              <div className={styles.notifItemTitle}>{n.title}</div>
                              <div className={styles.notifMsg}>{n.message}</div>
                              <div className={styles.notifTime}>{formatNotifDate(n.createdAt)}</div>
                            </div>
                            {n.status === 'unread' && <span className={styles.notifDot} />}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Dropdown menü */}
                {showDropdown && (
                  <div className={styles.dropdown}>
                    <div className={styles.dropdownHeader}>
                      <p className={styles.dropdownEmail}>{currentUser?.email}</p>
                    </div>
                    <div className={styles.dropdownDivider}></div>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/profile');
                      }}
                    >
                      <svg className={styles.dropdownItemIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {t('navbar.profile')}
                    </button>
                    {(isAdmin || isModerator) && (
                      <button
                        className={styles.dropdownItem}
                        onClick={() => {
                          setShowDropdown(false);
                          setShowNotifications(true);
                        }}
                      >
                        <svg className={styles.dropdownItemIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        Bildirimler
                        {unreadNotifications > 0 && (
                          <span className={styles.dropdownBadge}>
                            {unreadNotifications > 99 ? '99+' : unreadNotifications}
                          </span>
                        )}
                      </button>
                    )}
                    {isSeller && (
                      <button 
                        className={styles.dropdownItem}
                        onClick={() => {
                          setShowDropdown(false);
                          navigate('/seller-dashboard');
                        }}
                      >
                        <svg className={styles.dropdownItemIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {t('navbar.sellerPanel')}
                      </button>
                    )}
                    {isAdminOrModerator && (
                      <>
                        <div className={styles.dropdownDivider}></div>
                        <button 
                          className={styles.dropdownItem}
                          onClick={() => {
                            setShowDropdown(false);
                            navigate('/admin');
                          }}
                        >
                          <svg className={styles.dropdownItemIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {isAdmin ? t('navbar.adminPanel') : t('navbar.moderatorPanel')}
                        </button>
                      </>
                    )}
                    <div className={styles.dropdownDivider}></div>
                    <div className={styles.dropdownRow}>
                      <button onClick={() => { toggleTheme(); }} className={styles.dropdownToggleBtn}>
                        {theme === 'dark' ? '☀️' : '🌙'}
                      </button>
                      <button onClick={() => { toggleLanguage(); }} className={styles.dropdownToggleBtn}>
                        {language === 'tr' ? 'EN' : 'TR'}
                      </button>
                    </div>
                    <div className={styles.dropdownDivider}></div>
                    <button
                      className={`${styles.dropdownItem} ${styles.logoutItem}`}
                      onClick={handleLogout}
                    >
                      <svg className={styles.dropdownItemIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {t('navbar.signOut')}
                    </button>
                  </div>
                )}
              </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className={styles.mobileMenu}>
            <button 
              className={styles.menuButton}
              onClick={() => { setShowMobileMenu(!showMobileMenu); setShowDropdown(false); setShowNotifications(false); }}
            >
              <svg className={styles.menuIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu backdrop + panel */}
        {showMobileMenu && (
          <>
          <div className={styles.mobileBackdrop} onClick={() => setShowMobileMenu(false)} />
          <div className={styles.mobileMenuPanel}>
            {pageVisibility?.home !== false && (
              <Link
                to="/"
                className={`${styles.mobileLink} ${isActive('/') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {t('navbar.home')}
              </Link>
            )}
            {pageVisibility?.marketplace !== false && (
              <Link
                to="/marketplace"
                className={`${styles.mobileLink} ${isActive('/marketplace') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                {t('navbar.gallery')}
              </Link>
            )}
            {pageVisibility?.sellers !== false && (
              <Link
                to="/sellers"
                className={`${styles.mobileLink} ${isActive('/sellers') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {t('navbar.sellers')}
              </Link>
            )}
            {pageVisibility?.about !== false && (
              <Link
                to="/about"
                className={`${styles.mobileLink} ${isActive('/about') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('navbar.about')}
              </Link>
            )}
            {pageVisibility?.contact !== false && (
              <Link
                to="/contact"
                className={`${styles.mobileLink} ${isActive('/contact') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('navbar.contact')}
              </Link>
            )}

            <div className={styles.mobileDivider}></div>

            {/* Kullanıcı menüsü mobil */}
            {isAuthenticated ? (
              <>
                <div className={styles.mobileUserInfo}>
                  <div className={styles.userAvatar}>
                    {currentUser?.photoURL ? (
                      <img 
                        src={currentUser.photoURL} 
                        alt="Avatar" 
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span style={{ display: currentUser?.photoURL ? 'none' : 'flex' }}>
                      {getDisplayName().charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className={styles.mobileUserName}>{getDisplayName()}</div>
                    <div className={styles.mobileUserEmail}>{currentUser?.email}</div>
                  </div>
                </div>
                <Link
                  to="/profile"
                  className={styles.mobileLink}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('navbar.profile')}
                </Link>
                <Link
                  to="/mesajlar"
                  className={styles.mobileLink}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {t('navbar.messages')}
                  {unreadMessages > 0 && (
                    <span className={styles.mobileBadge}>
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </Link>
                {isSeller && (
                  <Link 
                    to="/seller-dashboard" 
                    className={styles.mobileLink}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {t('navbar.sellerPanel')}
                  </Link>
                )}
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className={styles.mobileLink}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t('navbar.adminPanel')}
                  </Link>
                )}
                <button
                  className={`${styles.mobileLink} ${styles.logoutLink}`}
                  onClick={() => {
                    handleLogout();
                    setShowMobileMenu(false);
                  }}
                >
                  <svg className={styles.mobileLinkIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('navbar.signOut')}
                </button>
              </>
            ) : (
              <button 
                className={styles.mobileLoginButton}
                onClick={() => {
                  handleLoginClick();
                  setShowMobileMenu(false);
                }}
              >
                {t('navbar.signIn')}
              </button>
            )}

            <div className={styles.mobileDivider}></div>

            <button
              onClick={() => {
                toggleTheme();
                setShowMobileMenu(false);
              }}
              className={styles.mobileThemeToggle}
            >
              {theme === 'dark' ? '☀️ Aydınlık Mod' : '🌙 Karanlık Mod'}
            </button>
            <button
              onClick={() => {
                toggleLanguage();
                setShowMobileMenu(false);
              }}
              className={styles.mobileThemeToggle}
            >
              {language === 'tr' ? '🌐 English' : '🌐 Türkçe'}
            </button>
          </div>
          </>
        )}
      </div>

    </nav>
  );
};

export default Navbar; 